import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useRegularizationMutations } from '../api'
import { REGULARIZATION_TYPE_LABELS } from '../types'
import type { RegularizationType } from '../types'
import { Select } from '@/components/ui/select'

/**
 * Asking for a day's attendance to be corrected.
 *
 * The two time boxes are optional individually and required together — a request that
 * proposes nothing cannot correct anything — and the form says which one the day is
 * actually missing rather than making the person work it out.
 */
export function RegularisationForm({
  workDate,
  employeeId,
  missing,
  onDone,
}: {
  workDate: string
  employeeId?: string
  missing?: RegularizationType
  onDone?: () => void
}) {
  const { submit } = useRegularizationMutations()
  const [requestType, setRequestType] = useState<RegularizationType>(missing ?? 'MISSING_OUT')
  const [firstIn, setFirstIn] = useState('')
  const [lastOut, setLastOut] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const needsIn = requestType !== 'MISSING_OUT'
  const needsOut = requestType !== 'MISSING_IN'

  /** Kept out of the JSX so the submit handler itself returns void, not a promise. */
  async function send() {
    setError(null)
    try {
      await submit.mutateAsync({
        employeeId,
        workDate,
        requestType,
        // Sent as an instant. The browser's zone is the site's zone in every deployment this
        // serves, and the backend recomputes the day through the rostered shift anyway.
        requestedFirstIn: firstIn ? new Date(`${workDate}T${firstIn}`).toISOString() : undefined,
        requestedLastOut: lastOut ? new Date(`${workDate}T${lastOut}`).toISOString() : undefined,
        reason,
      })
      onDone?.()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be submitted')
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        void send()
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="reg-type">What is wrong</Label>
        <Select
          id="reg-type"
          value={requestType}
          onChange={(event) => setRequestType(event.target.value as RegularizationType)}
          className="w-full"
        >
          {(Object.keys(REGULARIZATION_TYPE_LABELS) as RegularizationType[]).map((type) => (
            <option key={type} value={type}>
              {REGULARIZATION_TYPE_LABELS[type]}
            </option>
          ))}
        </Select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {needsIn && (
          <div className="space-y-1.5">
            <Label htmlFor="reg-in" required={requestType === 'MISSING_IN' || requestType === 'MISSING_BOTH'}>
              Time in
            </Label>
            <Input
              id="reg-in"
              type="time"
              required={requestType === 'MISSING_IN' || requestType === 'MISSING_BOTH'}
              value={firstIn}
              onChange={(event) => setFirstIn(event.target.value)}
            />
          </div>
        )}
        {needsOut && (
          <div className="space-y-1.5">
            <Label htmlFor="reg-out" required={requestType === 'MISSING_OUT' || requestType === 'MISSING_BOTH'}>
              Time out
            </Label>
            <Input
              id="reg-out"
              type="time"
              required={requestType === 'MISSING_OUT' || requestType === 'MISSING_BOTH'}
              value={lastOut}
              onChange={(event) => setLastOut(event.target.value)}
            />
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="reg-reason" required>
          What happened
        </Label>
        <textarea
          id="reg-reason"
          required
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Left through the service entrance; the terminal was down"
          className="w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm"
        />
        <p className="text-xs text-[var(--color-muted)]">
          Your manager sees this next to the punches the terminals recorded for the day.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      <Button type="submit" disabled={submit.isPending}>
        {submit.isPending ? 'Submitting…' : 'Submit for approval'}
      </Button>
    </form>
  )
}
